import { Stack } from '@mui/material';
import { useMemo } from 'react';
import { SelectElement, TextFieldElement, useFormContext, useWatch } from 'react-hook-form-mui';
import { useAiPromptsMetadata } from '../../../../../sic-lib/api/hooks/useAiPrompts';
import { aiPlanExpirationOptions } from '../../../../../sic-lib/defaults';
import { AiPromptFeatures } from '../../../../../sic-lib/types/Ai';
import AxiosErrorAlert from '../../../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../../../Shared/ButtonLoading';

interface Props {
    isLoading: boolean;
    isError: boolean;
    error: any;
    isSuccess: boolean;
}

export function CreateSmartReplyPlanFormControls({ isLoading, isError, error, isSuccess }: Props) {
    const {
        formState: { isValid },
    } = useFormContext();

    const { data: prompts, isLoading: isLoadingPrompts } = useAiPromptsMetadata();

    const promptOptions = useMemo(() => {
        if (isLoadingPrompts) return [{ id: 0, label: 'Loading...', disabled: true }];

        let result = [{ id: 0, label: 'None' }];

        if (prompts) {
            let filtered = prompts
                ?.filter(prompt => prompt.feature === AiPromptFeatures.ConversationSummary)
                ?.map(prompt => ({
                    id: prompt.promptId,
                    label: prompt.title,
                }));
            result = [...result, ...filtered];
        }

        return result;
    }, [prompts, isLoadingPrompts]);

    const clientId = useWatch({ name: 'clientId' }) as number | undefined;

    return (
        <Stack spacing={2} p={2}>
            <TextFieldElement name="name" label="Title" variant="outlined" fullWidth required />
            <TextFieldElement name="description" label="Description" variant="outlined" fullWidth />
            <TextFieldElement
                name="clientId"
                label="Client Id (empty for global)"
                variant="outlined"
                fullWidth
            />
            <TextFieldElement
                name="userName"
                label="UserName"
                variant="outlined"
                disabled={!clientId}
                fullWidth
            />
            <TextFieldElement
                name="followUpDelay"
                label="Auto-Reply Follow Up Delay (in seconds)"
                variant="outlined"
                type="number"
                validation={{
                    min: { value: 0, message: 'At least 0s' },
                    max: { value: 600, message: 'At most 10 mins (600s)' },
                }}
                fullWidth
            />
            <SelectElement
                name="completionPromptId"
                label="Completion Prompt"
                variant="outlined"
                fullWidth
                options={promptOptions}
            />
            <SelectElement
                name="expiresMins"
                label="Plan Expires"
                variant="outlined"
                fullWidth
                options={aiPlanExpirationOptions}
            />
            <AxiosErrorAlert error={error} />
            <ButtonLoading
                isLoading={isLoading}
                isError={isError}
                isSuccess={isSuccess}
                variant="contained"
                color="success"
                type="submit"
                disabled={!isValid}
                fullWidth
            >
                Create
            </ButtonLoading>
        </Stack>
    );
}
